import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import ChevronButton from 'src/shared/components/chevron-button/ChevronButton';
import Chevron from 'src/shared/components/chevron/Chevron';
import chevronBtnStyles from 'src/shared/components/chevron-button/ChevronButton.module.scss';
import Input from 'src/shared/components/input/Input';

import styles from './Pagination.module.scss';

function JumpChevronButton(props: { disabled: boolean; onClick: () => void; 'aria-label': string; direction: 'left' | 'right' }) {
    const { disabled, onClick, direction } = props;
    return (
        <button
            type='button'
            className={classNames(
                chevronBtnStyles.chevronButton,
                chevronBtnStyles.button,
                { [chevronBtnStyles.chevronButtonDisabled]: disabled },
                styles.chevron,
                styles.jumpChevronButton
            )}
            disabled={disabled}
            onClick={onClick}
            aria-label={props['aria-label']}
        >
            <span className={styles.jumpChevronPair}>
                <Chevron direction={direction} animate={false} variation='filled' />
                <Chevron direction={direction} animate={false} variation='filled' />
            </span>
        </button>
    );
}

export type PaginationProps = {
    currentPageNumber: number;
    lastPageNumber: number;
    onChange: (pageNumber: number) => void;
    className?: string;
};

const Pagination = (props: PaginationProps) => {
    const { currentPageNumber, lastPageNumber, className } = props;
    const [pageNumber, setPageNumber] = useState<number | ''>(currentPageNumber);

    useEffect(() => {
        setPageNumber(currentPageNumber);
    }, [currentPageNumber]);

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pageNumberFromInput = Number(event.target.value);

        if (event.target.value === '') {
            setPageNumber('');
            return;
        }

        if (isNaN(pageNumberFromInput) || pageNumberFromInput > lastPageNumber || pageNumberFromInput < 1) {
            event.preventDefault();
            return;
        }

        setPageNumber(pageNumberFromInput);
        props.onChange(pageNumberFromInput ? pageNumberFromInput : 1);
    };

    const onBlur = () => {
        setPageNumber(pageNumber ? pageNumber : 1);
        props.onChange(pageNumber ? pageNumber : 1);
    };

    const singlePage = lastPageNumber <= 1;

    return (
        <div className={`${styles.pagination} ${className ?? ''}`}>
            <JumpChevronButton
                disabled={singlePage || currentPageNumber <= 1}
                onClick={() => props.onChange(1)}
                aria-label='First page'
                direction='left'
            />
            <ChevronButton
                direction='left'
                className={styles.chevron}
                disabled={currentPageNumber <= 1}
                onClick={() => props.onChange(currentPageNumber - 1)}
                aria-label='Previous page'
            />
            <div className={styles.inputWrapper}>
                <Input
                    value={pageNumber}
                    onBlur={onBlur}
                    onChange={onChange}
                    className={styles.inputBox}
                    disabled={singlePage}
                    data-testid='page-number-input'
                />
            </div>
            of {lastPageNumber}
            <ChevronButton
                direction='right'
                className={styles.chevron}
                disabled={currentPageNumber >= lastPageNumber}
                onClick={() => props.onChange(currentPageNumber + 1)}
                aria-label='Next page'
            />
            <JumpChevronButton
                disabled={singlePage || currentPageNumber >= lastPageNumber}
                onClick={() => props.onChange(lastPageNumber)}
                aria-label='Last page'
                direction='right'
            />
        </div>
    );
};

export default Pagination;
